import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { zoneListService, ZoneFilters } from '../services/zoneList.service';

export function useZoneList() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const [filters, setFilters] = useState<ZoneFilters>({
    search: '',
    projectId: 'all',
    status: 'all',
  });

  const projectsQuery = useQuery({
    queryKey: ['projects', 'simple-list', companyId],
    queryFn: () => zoneListService.getProjects(companyId),
    enabled: !!companyId,
  });

  const zonesQuery = useQuery({
    queryKey: ['zones', 'list', companyId, filters],
    queryFn: () => zoneListService.getZones(companyId, filters),
    enabled: !!companyId,
  });

  // Calculate stats
  const stats = useMemo(() => {
    const data = zonesQuery.data || [];
    const total = data.length;
    const active = data.filter(z => z.status_code === 'active').length;
    const paused = data.filter(z => z.status_code === 'paused').length;
    const completed = data.filter(z => z.status_code === 'completed').length;

    const getPct = (val: number) => total > 0 ? ((val / total) * 100).toFixed(1) + '%' : '0%';

    return {
      total,
      active,
      activePct: getPct(active),
      paused,
      pausedPct: getPct(paused),
      completed,
      completedPct: getPct(completed),
    };
  }, [zonesQuery.data]);

  const setFilter = (key: keyof ZoneFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const refresh = () => {
    zonesQuery.refetch();
  };

  return {
    data: zonesQuery.data || [],
    isLoading: zonesQuery.isLoading,
    projects: projectsQuery.data || [],
    filters,
    setFilter,
    stats,
    refresh
  };
}
